import { Link, useParams } from "react-router-dom";
import Icon from "../components/Icon";
import SingleMarkerMap from "../components/SingleMarkerMap";
import { getCommunityById, getGuildById } from "../data";
import { formatDate, formatNumber } from "../utils/format";

const formatHost = (url) => {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
};

const CommunityProfile = () => {
  const { communityId } = useParams();
  const community = getCommunityById(communityId);
  const guild = community ? getGuildById(community.guildId) : null;

  if (!community) {
    return (
      <div className="space-y-4">
        <h1 className="text-3xl text-slate-100">Community not found</h1>
        <Link to="/directory" className="text-brand-primary underline-offset-4 hover:underline">
          Return to directory
        </Link>
      </div>
    );
  }

  const primaryPlatform = community.platforms[0];

  return (
    <div className="space-y-10">
      <section className="panel space-y-5">
        <div className="flex flex-wrap items-center gap-3 text-xs uppercase tracking-[0.35em] text-brand-steel">
          <span className="chip chip-soft capitalize">{community.status}</span>
          <span>{community.region}</span>
          <span>Launched {formatDate(community.launched)}</span>
        </div>
        <div className="space-y-4">
          <p className="eyebrow">Community profile</p>
          <h1 className="text-4xl text-slate-100">{community.name}</h1>
          <p className="text-base text-slate-200">{community.summary}</p>
          <div className="flex flex-wrap gap-4 text-sm text-brand-steel">
            <span>{community.city}</span>
            <span>• {community.region}</span>
            <span>• Cadence: {community.activity}</span>
          </div>
          {guild && (
            <Link
              to={`/guilds/${guild.id}`}
              className="inline-flex text-sm text-brand-secondary underline-offset-4 hover:underline"
            >
              Part of {guild.name}
            </Link>
          )}
        </div>
        <div className="flex flex-wrap gap-3 text-sm">
          {primaryPlatform && (
            <a href={primaryPlatform.url} target="_blank" rel="noreferrer" className="btn-primary">
              Visit {primaryPlatform.type}
            </a>
          )}
          {guild && (
            <Link to={`/guilds/${guild.id}`} className="btn-secondary">
              View guild hub
            </Link>
          )}
          <Link to="/map" className="btn-secondary">
            Global map
          </Link>
        </div>
      </section>

      <div className="section-wrapper">
        <section className="grid gap-5 md:grid-cols-3">
          {[
            { label: "Members", value: formatNumber(community.memberCount), icon: "shield" },
            { label: "Activity cadence", value: community.activity, icon: "layers" },
            { label: "Verification", value: community.status, icon: "compass" }
          ].map((item) => (
            <article key={item.label} className="panel space-y-2">
              <p className="flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-brand-steel">
                <Icon name={item.icon} className="h-4 w-4 text-brand-primary" />
                {item.label}
              </p>
              <p className="text-2xl font-semibold text-brand-highlight capitalize">{item.value}</p>
            </article>
          ))}
        </section>
      </div>

      <div className="section-wrapper">
        <section className="grid gap-6 lg:grid-cols-3">
          <article className="panel space-y-4 lg:col-span-2">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-brand-steel">Platforms</p>
              <div className="mt-3 space-y-3">
                {community.platforms.map((platform) => (
                  <a
                    key={platform.url}
                    href={platform.url}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center justify-between rounded-2xl border border-brand-ink/50 bg-brand-night/70 px-4 py-4 text-base text-slate-100 transition hover:border-brand-primary"
                  >
                    <div>
                      <p className="text-xs uppercase tracking-[0.3em] text-brand-steel">{platform.type}</p>
                      <p className="text-slate-300">{formatHost(platform.url)}</p>
                    </div>
                    <span className="text-xs uppercase tracking-[0.3em] text-brand-secondary">Open</span>
                  </a>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-brand-steel">Tags</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {community.tags.map((tag) => (
                  <span key={tag} className="chip">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </article>
          <article className="panel space-y-3">
            <p className="text-xs uppercase tracking-[0.3em] text-brand-steel">Map highlight</p>
            <SingleMarkerMap position={[community.lat, community.lng]} className="h-72" />
          </article>
        </section>
      </div>

      <div className="section-wrapper">
        <section className="panel space-y-3 text-base text-slate-300">
          <p className="text-slate-100">How to join</p>
          <p>
            Follow the platform links above for self-serve access. If you need an introduction or verification support,
            contact the guild curator through the guild page or submit a request via the submission form.
          </p>
        </section>
      </div>
    </div>
  );
};

export default CommunityProfile;
